import { RealtimeClientConfig } from '../types'

export const ADA_AUDIO_RESPONSE_EVENT_TYPES = new Set([
  'response.audio.delta',
  'response.output_audio.delta',
  'response.audio_transcript.delta',
  'response.output_audio_transcript.delta',
])

export const isAdaAudioResponseEvent = (eventType: string) => ADA_AUDIO_RESPONSE_EVENT_TYPES.has(eventType)

export const VOICE_REASONING_STATUS_FALLBACK = 'Ada is preparing a response...'

export const REASONING_TEXT_DELTA_EVENT_TYPES = new Set([
  'response.reasoning.delta',
  'response.reasoning_summary.text.delta',
])

export type OutputItemPhase = 'commentary' | 'final_answer' | 'unknown'

export const getOutputItemPhase = (item: { phase?: string } | null | undefined): OutputItemPhase => {
  if (item?.phase === 'commentary') {
    return 'commentary'
  }
  if (item?.phase === 'final_answer') {
    return 'final_answer'
  }
  return 'unknown'
}

export const isResponseLifecycleStartEvent = (eventType: string) =>
  eventType === 'response.created' || eventType === 'response.in_progress'

export const extractReasoningTextDelta = (event: { type?: string; delta?: string }): string | null => {
  if (!event?.type || !REASONING_TEXT_DELTA_EVENT_TYPES.has(event.type)) {
    return null
  }
  const delta = event.delta
  return typeof delta === 'string' && delta.length > 0 ? delta : null
}

export const shouldClearPreambleForAssistantSpeech = (eventType: string) =>
  eventType === 'response.output_audio.delta' ||
  eventType === 'response.audio.delta' ||
  eventType === 'response.output_audio_transcript.delta' ||
  eventType === 'response.audio_transcript.delta'

export const isAssistantAudioPlaybackEvent = (eventType: string) =>
  eventType === 'response.output_audio.delta' ||
  eventType === 'response.audio.delta' ||
  eventType === 'output_audio_buffer.started'

export const isAssistantAudioPlaybackStoppedEvent = (eventType: string) =>
  eventType === 'output_audio_buffer.stopped' ||
  eventType === 'output_audio_buffer.cleared' ||
  eventType === 'response.cancelled' ||
  eventType === 'response.canceled'

export const isFunctionCallOnlyResponseDone = (response: { output?: any[] }): boolean => {
  const outputs = response?.output || []
  return outputs.length > 0 && outputs.every((item) => item?.type === 'function_call')
}

export const extractMessageContentText = (content: {
  type?: string
  transcript?: string
  text?: string | { value?: string }
}): string => {
  if (!content) {
    return ''
  }
  if (typeof content.transcript === 'string' && content.transcript.trim()) {
    return content.transcript
  }
  if (content.type === 'output_text' || content.type === 'text') {
    const text = content.text
    if (typeof text === 'string') {
      return text
    }
    if (text && typeof text.value === 'string') {
      return text.value
    }
  }
  if (content.type === 'audio' || content.type === 'output_audio') {
    return content.transcript || ''
  }
  return ''
}

export const isAssistantTranscriptFragmentEvent = (event: {
  type?: string
  delta?: string
  transcript?: string
}): event is { type: string; delta?: string; transcript?: string } =>
  (event.type === 'response.output_audio_transcript.delta' && typeof event.delta === 'string') ||
  (event.type === 'response.audio_transcript.delta' && typeof event.delta === 'string') ||
  (event.type === 'response.output_audio_transcript.done' && typeof event.transcript === 'string') ||
  (event.type === 'response.audio_transcript.done' && typeof event.transcript === 'string')

/** Advance displayed live caption toward the streamed target (word-at-a-time when possible). */
export const revealNextSpeakingTranscriptChunk = (displayed: string, target: string): string => {
  if (displayed.length >= target.length) {
    return displayed
  }
  const remainder = target.slice(displayed.length)
  const wordMatch = remainder.match(/^(\s*\S+)/)
  if (wordMatch?.[1]) {
    return displayed + wordMatch[1]
  }
  return displayed + remainder.charAt(0)
}

export const getAssistantTranscriptFragment = (event: {
  type?: string
  delta?: string
  transcript?: string
}): string => {
  if (event.type?.endsWith('.delta') && typeof event.delta === 'string') {
    return event.delta
  }
  if (event.type?.endsWith('.done') && typeof event.transcript === 'string') {
    return event.transcript
  }
  return ''
}

const suffixPrefixOverlapLength = (current: string, fragment: string): number => {
  const max = Math.min(current.length, fragment.length)
  for (let len = max; len > 0; len -= 1) {
    if (current.endsWith(fragment.slice(0, len))) {
      return len
    }
  }
  return 0
}

/**
 * Merge streamed transcript fragments without duplicating cumulative deltas or final .done payloads.
 */
export const mergeAssistantTranscriptFragment = (current: string, fragment: string): string => {
  if (!fragment) {
    return current
  }
  if (!current) {
    return fragment
  }
  if (fragment === current) {
    return current
  }
  if (current.endsWith(fragment)) {
    return current
  }
  if (fragment.startsWith(current)) {
    return fragment
  }
  if (current.startsWith(fragment)) {
    return current
  }
  const overlap = suffixPrefixOverlapLength(current, fragment)
  if (overlap > 0) {
    return current + fragment.slice(overlap)
  }
  return current + fragment
}

export const extractAssistantTranscriptsFromResponseDone = (response: { output?: any[] }): string => {
  const parts: string[] = []
  for (const output of response?.output || []) {
    if (output?.type !== 'message' || !Array.isArray(output.content)) {
      continue
    }
    let messageText = ''
    for (const content of output.content) {
      messageText += extractMessageContentText(content)
    }
    const trimmed = messageText.trim()
    if (trimmed) {
      parts.push(trimmed)
    }
  }
  return parts.join(' ').trim()
}

export const buildGaSessionUpdate = (sessionFields: Record<string, unknown>) => ({
  type: 'session.update',
  session: {
    type: 'realtime',
    ...sessionFields,
  },
})

/** Server VAD tuned for barge-in: lower threshold so user speech is detected over assistant audio. */
export const DEFAULT_TURN_DETECTION = {
  type: 'server_vad',
  threshold: 0.7,
  prefix_padding_ms: 300,
  silence_duration_ms: 600,
  create_response: false,
  interrupt_response: true,
}

export const buildSessionAudioInput = (
  inputAudioTranscription: RealtimeClientConfig['input_audio_transcription'],
  turnDetection?: Record<string, unknown>
) => ({
  input: {
    transcription: inputAudioTranscription,
    ...(turnDetection ? { turn_detection: turnDetection } : {}),
  },
})

export const EMPTY_REALTIME_CLIENT_CONFIG: RealtimeClientConfig = {
  tools: [],
  instructions: '',
  model: '',
  input_audio_transcription: { language: 'en', model: 'gpt-realtime-whisper' },
  base_url: '',
  language: 'en',
}

export type RealtimeFunctionCall = {
  type: 'function_call'
  name: string
  call_id: string
  arguments: string
}

/** Execute tools only after the full response is materialized (avoids racing in-flight audio). */
export const TOOL_EXECUTION_EVENT_TYPES = new Set(['response.done'])

export const isToolExecutionEvent = (eventType: string) => TOOL_EXECUTION_EVENT_TYPES.has(eventType)

/** Collect function calls from GA realtime events (may include multiple per response). */
export const extractFunctionCallsFromEvent = (event: any): RealtimeFunctionCall[] => {
  if (event.type === 'response.done') {
    return (event.response?.output || []).filter((item: any) => item?.type === 'function_call')
  }
  if (event.type === 'response.output_item.done' && event.item?.type === 'function_call') {
    return [event.item]
  }
  if (event.type === 'response.function_call_arguments.done') {
    return [
      {
        type: 'function_call',
        name: event.name,
        call_id: event.call_id,
        arguments: event.arguments,
      },
    ]
  }
  return []
}
